/**
 * Generic modal overlay with backdrop blur and centered content card.
 * On mobile (<768px), renders as a bottom sheet for better thumb reach.
 *
 * Props:
 *  - onClose: called when backdrop clicked, X pressed, or Escape hit
 *  - title: string shown in the modal header
 *  - children: modal body content
 *  - cardBg / cardBorder / textColor: theme-aware colors
 */
import { useRef } from 'react';
import { useIsMobile } from '@/shared/hooks/useMediaQuery';
import { useModalA11y } from '@/shared/hooks/useModalA11y';

export default function Modal({ onClose, title, children, cardBg = "white", cardBorder = "#eee", textColor = "#0f172a" }) {
  const mobile = useIsMobile();
  const cardRef = useRef(null);

  useModalA11y({ onClose, containerRef: cardRef });

  const containerAlign = mobile
    ? { alignItems: "flex-end" }
    : { alignItems: "center" };

  const cardStyle = mobile
    ? { position: "relative", background: cardBg, borderRadius: "20px 20px 0 0", boxShadow: "0 -8px 40px rgba(0,0,0,0.2)", width: "100%", maxHeight: "92vh", overflowY: "auto" }
    : { position: "relative", background: cardBg, borderRadius: 20, boxShadow: "0 25px 60px rgba(0,0,0,0.2)", maxWidth: 440, width: "92%", maxHeight: "85vh", overflowY: "auto" };

  return (
    <div role="dialog" aria-modal="true" aria-label={title} style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", justifyContent: "center", ...containerAlign }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }} />
      <div ref={cardRef} style={cardStyle}>
        {mobile && (
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 0" }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: cardBorder }} />
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px", borderBottom: "1px solid " + cardBorder }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: textColor }}>{title}</h3>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#94a3b8", lineHeight: 1, minHeight: 44, minWidth: 44, display: "flex", alignItems: "center", justifyContent: "center" }}>{"\u00D7"}</button>
        </div>
        <div style={{ padding: 22, display: "flex", flexDirection: "column", gap: 14 }}>
          {children}
        </div>
      </div>
    </div>
  );
}
